import { type UnifiedTransfer } from '@column/api';

import { getProgressFromTransferStatus } from './progress';
import { getStatusBadgeTone } from './status';
import { TransferProgress, TransferStatus, parseTransferStatus } from './types';
import { BadgeTone } from './utils';

export type BatchStatusCount = { status: string; count: number };
export type BatchStatusGroup = { progress: TransferProgress; tone: BadgeTone; count: number };

// Lifecycle order of the external ACH and check statuses batch members can have.
const CHRONOLOGICAL_STATUSES: string[] = [
  TransferStatus.PRE_REVIEW,
  TransferStatus.HOLD,
  TransferStatus.SCHEDULED,
  TransferStatus.INITIATED,
  TransferStatus.ISSUED,
  TransferStatus.MANUAL_REVIEW,
  TransferStatus.MANUAL_REVIEW_APPROVED,
  TransferStatus.PENDING_SUBMISSION,
  TransferStatus.SUBMITTED,
  TransferStatus.PENDING_DEPOSIT,
  TransferStatus.DEPOSITED,
  TransferStatus.PENDING_STOP,
  TransferStatus.SETTLED,
  TransferStatus.COMPLETED,
  TransferStatus.PENDING_FIRST_RETURN,
  TransferStatus.FIRST_RETURN,
  TransferStatus.PENDING_RECLEAR,
  TransferStatus.RECLEARED,
  TransferStatus.PENDING_SECOND_RETURN,
  TransferStatus.SECOND_RETURN,
  TransferStatus.PENDING_RETURN,
  TransferStatus.PENDING_USER_INITIATED_RETURN,
  TransferStatus.USER_INITIATED_RETURN_SUBMITTED,
  TransferStatus.USER_INITIATED_RETURNED,
  TransferStatus.RETURNED,
  TransferStatus.RETURN_DISHONORED,
  TransferStatus.USER_INITIATED_RETURN_DISHONORED,
  TransferStatus.RETURN_CONTESTED,
  TransferStatus.STOPPED,
  TransferStatus.REJECTED,
  TransferStatus.CANCELED,
];

const chronologicalRank = (status: string): number => {
  const rank = CHRONOLOGICAL_STATUSES.indexOf(status.toLowerCase());
  return rank === -1 ? CHRONOLOGICAL_STATUSES.length : rank;
};

const statusAppearance = (status: string) => {
  const parsed = parseTransferStatus(status);
  const progress = getProgressFromTransferStatus(parsed);
  return { progress, tone: getStatusBadgeTone(progress, parsed) };
};

export const getTransferStatusTone = (status: string): BadgeTone => statusAppearance(status).tone;

// Member counts for a batch row in lifecycle order; empty until the batch's members are indexed.
export const getBatchStatusCounts = (row: Pick<UnifiedTransfer, 'batch_status_counts'>): BatchStatusCount[] =>
  Object.entries(row.batch_status_counts ?? {})
    .filter(([, count]) => count > 0)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => chronologicalRank(a.status) - chronologicalRank(b.status) || a.status.localeCompare(b.status));

// Merges statuses that render with the same icon and color, keeping the position of the earliest one.
export const groupBatchStatusCounts = (counts: BatchStatusCount[]): BatchStatusGroup[] => {
  const groups = new Map<string, BatchStatusGroup>();
  for (const { status, count } of counts) {
    const { progress, tone } = statusAppearance(status);
    const key = `${progress}:${tone}`;
    const group = groups.get(key);
    if (group) {
      group.count += count;
    } else {
      groups.set(key, { progress, tone, count });
    }
  }
  return [...groups.values()];
};
